import { getSelectedUser } from './../user/user.selectors';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { selectRouteParam } from '../router/router.selector';

import * as fromAccount from './account.reducer';
import * as _ from 'lodash';
import { find, size } from "lodash";
import { getSelectedPriceList } from '../priceList/priceListv2.selectors';
import { AccountUsersResponse } from '../../@shared/models/account_users_response.model';

const {selectEntities} = fromAccount.adapter.getSelectors();

export const selectAccountState = createFeatureSelector<fromAccount.State>(fromAccount.accountFeatureKey);

const listSelectors = fromAccount.listAdapter.getSelectors((state: fromAccount.State) => state.list);
const favsSelectors = fromAccount.listAdapter.getSelectors((state: fromAccount.State) => state.favs);

export const getAccountsTotalInStore = createSelector(selectAccountState, listSelectors.selectTotal);

/**
 * Get list config (sorting etc.)
 */
export const getAccountListConfig = createSelector(selectAccountState, (state: fromAccount.State) => _.get(state, ['list', 'listConfig'], {}));

/**
 * Get Account list
 */
export const getAccounts = createSelector(selectAccountState, listSelectors.selectAll);

/**
 * Get Favorite Account list
 */
export const getFavorites = createSelector(selectAccountState, favsSelectors.selectAll);

/**
 * Get contracts not associated with user
 */
export const getAvailableAccounts = createSelector(getAccounts, getSelectedUser, (accounts, user) =>
  accounts.filter(account => size(find(user?.relations?.contracts, { contract_uuid: account.object_uuid })) === 0)
);

/**
 * Get contracts not associated with user
 */
export const getAvailableAccountsForPriceList = createSelector(getAccounts, getSelectedPriceList, (accounts, priceList) => {
    const ret: typeof accounts = [];
    accounts.forEach(account => {
        if (priceList?.data.id !== account.object_uuid) {
            ret.push(account);
        }
    });
    return ret;
});

/**
 * Get Account list meta data (total etc.)
 */
export const getAccountsMeta = createSelector(selectAccountState, (state: fromAccount.State) => _.get(state, ['list', 'meta'], { total: 0, count: 0, offset: 0, limit: 0, page: 0 }));

/**
 * Get Account list links, to load next bunch of data
 */
export const getAccountsLinks = createSelector(selectAccountState, (state: fromAccount.State) => _.get(state, ['list', 'links']));

/**
 * Get if Account list is loading
 */
export const isAccountListLoading = createSelector(selectAccountState, (state: fromAccount.State) => _.get(state, ['list', 'loading'], false));

/**
 * Get if Account list is loading next bunch of data
 */
export const isAccountListLoadingNext = createSelector(selectAccountState, (state: fromAccount.State) => _.get(state, ['list', 'loadingNext'], false));

/**
 * get if Account list is loaded
 */
export const isAccountListLoaded = createSelector(selectAccountState, (state: fromAccount.State) => _.get(state, ['list', 'loaded'], false));


/**
 * Get if Favorite Accounts list is loading
 */
 export const isFavAccountListLoading = createSelector(selectAccountState, (state: fromAccount.State) => _.get(state, ['favs', 'loading'], false));

 /**
  * get if Favorite Accounts list is loaded
  */
 export const isFavAccountListLoaded = createSelector(selectAccountState, (state: fromAccount.State) => _.get(state, ['favs', 'loaded'], false));


/**
 * get Account
 */
export const getAccount = (props: { uuid: string }) => createSelector(selectAccountState, (state: fromAccount.State) => _.get(selectEntities(state), [props.uuid]));

/**
 * get Account last error
 */
export const getAccountError = (props: { uuid: string }) => createSelector(selectAccountState, (state: fromAccount.State) => _.get(state, ['error', props.uuid]));

/**
 * get if Account is loading
 */
export const isAccountLoading = (props: { uuid: string }) => createSelector(selectAccountState, (state: fromAccount.State) => _.get(state, ['loading', props.uuid], false));

/**
 * get if Account is patching
 */
export const isAccountPatching = (props: { uuid: string }) => createSelector(selectAccountState, (state: fromAccount.State) => _.get(state, ['patching', props.uuid], false));

/**
 * get if Account is deleting
 */
export const isAccountDeleting = (props: { uuid: string }) => createSelector(selectAccountState, (state: fromAccount.State) => _.get(state, ['deleting', props.uuid], false));

/**
 * get if Account is ready loaded
 */
export const isAccountLoaded = (props: { uuid: string }) => createSelector(selectAccountState, (state: fromAccount.State) => _.get(state, ['loaded', props.uuid], false));

/*
export const getAccountUsers = (props: { uuid: string }) =>
    createSelector(selectAccountState, (state: fromAccount.State) => _.get(state, ['user_relations', props.uuid, 'data'], []));
*/
export const getAccountUsers = (props: { uuid: string }) =>
    createSelector(selectAccountState, (state: fromAccount.State) => _.get(state, ['user_relations', props.uuid, 'data'], undefined));

export const isAccountUsersLoading = (props: { uuid: string }) => createSelector(selectAccountState, (state: fromAccount.State) =>
    _.get(state, ['user_relations', props.uuid, 'loading'], false)
);

export const isAccountUsersLoaded = (props: { uuid: string }) => createSelector(selectAccountState, (state: fromAccount.State) =>
    _.get(state, ['user_relations', props.uuid, 'loaded'], false)
);

export const getAccountPriceListUuid = (props: { uuid: string }) =>
    createSelector(selectAccountState, (state: fromAccount.State) => {
        return _.get(selectEntities(state), [props.uuid, 'management_data', 'price_list_uuid'], null);
    });

/**
 * get selected Account (by route param)
 */
export const getSelectedAccount = createSelector(selectAccountState, selectRouteParam('account_uuid'), (state: fromAccount.State, account_uuid) =>
    _.get(selectEntities(state), [account_uuid])
);
export const getSelectedAccountName = createSelector(selectAccountState, selectRouteParam('account_uuid'), (state: fromAccount.State, account_uuid) => {
    return _.get(selectEntities(state), [account_uuid, 'name']) || _.get(state, ['list', 'entities', account_uuid, 'name']);
});
/**
 * get selected Account (by route param) last error
 */
export const getSelectedAccountError = createSelector(selectAccountState, selectRouteParam('account_uuid'), (state: fromAccount.State, account_uuid) =>
    _.get(state, ['error', account_uuid])
);

/**
 * get if selected Account (by route param) is loading
 */
export const isSelectedAccountLoading = createSelector(selectAccountState, selectRouteParam('account_uuid'), (state: fromAccount.State, account_uuid) =>
    _.get(state, ['loading', account_uuid], false)
);

/**
 * get if selected Account (by route param) is patching
 */
export const isSelectedAccountPatching = createSelector(selectAccountState, selectRouteParam('account_uuid'), (state: fromAccount.State, account_uuid) =>
    _.get(state, ['patching', account_uuid], false)
);

/**
 * get if selected Account (by route param) is deleting
 */
export const isSelectedAccountDeleting = createSelector(selectAccountState, selectRouteParam('account_uuid'), (state: fromAccount.State, account_uuid) =>
    _.get(state, ['deleting', account_uuid], false)
);

/**
 * get if selected Account (by route param) is ready loaded
 */
export const isSelectedAccountLoaded = createSelector(selectAccountState, selectRouteParam('account_uuid'), (state: fromAccount.State, account_uuid) =>
    _.get(state, ['loaded', account_uuid], false)
);

/**
 * Return all Contracts Projects
 */
export const getAccountRelations = createSelector(selectAccountState, (state: fromAccount.State) => _.get(state, ['accountRelations']));
export const getAccountRelation = (props: { account_uuid: string }) =>
    createSelector(selectAccountState, (state: fromAccount.State) => _.get(state, ['accountRelations', props.account_uuid]));
export const getAccountProjects = createSelector(selectAccountState, (state: fromAccount.State) => _.get(state, ['accountProjects']));
export const getAccountProject = (props: { account_uuid: string }) =>
    createSelector(selectAccountState, (state: fromAccount.State) => _.get(state, ['accountProjects', props.account_uuid]));
export const isAccountProjectsLoading = createSelector(selectAccountState, (state: fromAccount.State) => _.get(state, ['accountProjectsCounter']) == 0);
export const isAccountProjectsLoaded = createSelector(selectAccountState, (state: fromAccount.State) => _.get(state, ['accountProjectsLoaded']));

/**
 * get current search term
 */
export const getSearch = createSelector(selectAccountState, (state: fromAccount.State) => _.get(state, ['list', 'search'], ''));

/**
 * get current Account Users
 */
export const getSelectedAccountUsers = createSelector(selectAccountState, selectRouteParam('account_uuid'), (state: fromAccount.State, account_uuid) =>
  _.get(state, ['user_relations', account_uuid, 'data'], []) as AccountUsersResponse['user_relations']
);
export const isSelectedAccountUsersLoading = createSelector(selectAccountState, selectRouteParam('account_uuid'), (state: fromAccount.State, account_uuid) =>
    _.get(state, ['user_relations', account_uuid, 'loading'], false)
);

export const isSelectedAccountUsersLoaded = createSelector(selectAccountState, selectRouteParam('account_uuid'), (state: fromAccount.State, account_uuid) =>
    _.get(state, ['user_relations', account_uuid, 'loaded'], false)
);

export const getSelectedAccountProjects = createSelector(selectAccountState, selectRouteParam('account_uuid'), (state: fromAccount.State, account_uuid) =>
    _.get(selectEntities(state), [account_uuid])?.relations?.projects || []
);

export const getSelectedAccountPriceListUuid = createSelector(selectAccountState, selectRouteParam('account_uuid'), (state: fromAccount.State, account_uuid) => {
    return _.get(selectEntities(state), [account_uuid, 'management_data', 'price_list_uuid'], null);
});

/**
 * Get total number of contracts
 */

export const getAccountsTotal = createSelector(selectAccountState, (state: fromAccount.State) => _.get(state, ['total'], 0));

/**
 * get if Account total is loading
 */
export const isAccountTotalLoading = createSelector(selectAccountState, (state: fromAccount.State) => _.get(state, ['loadingTotal'], false));

/**
 * get if Account total is loaded
 */
export const isAccountTotalLoaded = createSelector(selectAccountState, (state: fromAccount.State) => _.get(state, ['loadedTotal'], false));

/**
 * get current Account Feature Flags
 */
export const getSelectedAccountFeatureFlags = createSelector(selectAccountState, selectRouteParam('account_uuid'), (state: fromAccount.State, account_uuid) =>
    _.get(state, ['account_feature_flags', account_uuid, 'data'], [])
);
export const isSelectedAccountFeatureFlagsLoading = createSelector(selectAccountState, selectRouteParam('account_uuid'), (state: fromAccount.State, account_uuid) =>
    _.get(state, ['account_feature_flags', account_uuid, 'loading'], false)
);

export const isSelectedAccountFeatureFlagsLoaded = createSelector(selectAccountState, selectRouteParam('account_uuid'), (state: fromAccount.State, account_uuid) =>
    _.get(state, ['account_feature_flags', account_uuid, 'loaded'], false)
);

export const getSelectedAccountLocationFeatureFlags = createSelector(selectAccountState, selectRouteParam('account_uuid'), (state: fromAccount.State, account_uuid) =>
    _.filter(_.get(state, ['account_feature_flags', account_uuid, 'data'], []), _feature => {
        return _feature.name.search('location_access_') >= 0;
    })
);
export const getSelectedAccountBackupLocationFeatureFlags = createSelector(selectAccountState, selectRouteParam('account_uuid'), (state: fromAccount.State, account_uuid) =>
  _.filter(_.get(state, ['account_feature_flags', account_uuid, 'data'], []), _feature => {
    return _feature.name.search('backup_location_') >= 0;
  })
);

export const getBillingAddress = createSelector(getSelectedAccount, account => account?.billing_address)
